import {
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Tooltip,
} from '@material-ui/core';
import React, { FunctionComponent, JSX } from 'react';

import { Text, TKey } from '~/src/dash/publicaUI';
import { compareAlphanums } from '~/src/dash/utils/basic';
import { errorColor } from '~/src/dash/utils/material-dashboard-react';
import { SelectDropdownWithSubheaderView } from '~/src/dash/views/select-dropdown-with-subheader-view';

export interface ISingleSelectOption {
  label: string;
  value: string;
  subheader?: string;
  tKey?: TKey;
}

export interface ISingleSelect {
  items: ISingleSelectOption[];
  value: string;
  label: string | React.ReactNode;
  name: string;
  onChange: (name: string, value: string | string[] | number) => void;
  maxItems?: number;
  helperText?: string | JSX.Element;
  error?: boolean;
  filter?: string;
  filterHelperText?: string;
  style?: Record<string, string>;
  sortComparefn?: (a: ISingleSelectOption, b: ISingleSelectOption) => number;
  disableAlphanumSort?: boolean;
  customMenuHeight?: number;
  itemWidth?: string | number; // used to determine when to cut off item label text, value should be lower than the width of the dropdown
  isItemTooltipDisabled?: boolean;
}

const useStyles = makeStyles(() => ({
  menuPaper: {
    maxHeight: 380,
  },
}));

const re = /[\/\._\- ]/g;
function cleanup(s: string): string {
  return (s || '').toLowerCase().replace(re, '');
}

export const SingleSelect: FunctionComponent<ISingleSelect> = ({
  items = [],
  value = '',
  label,
  name,
  onChange,
  maxItems,
  helperText,
  error,
  filter,
  style = {},
  sortComparefn = compareAlphanums, // set to undefined to disable sorting
  disableAlphanumSort = false,
  customMenuHeight,
  itemWidth,
  isItemTooltipDisabled,
}) => {
  const cleanFilter = filter ? cleanup(filter) : '';
  const useCustomStyle = makeStyles(() => ({
    menuPaper: {
      maxHeight: customMenuHeight,
    },
  }));
  const classes = customMenuHeight ? useCustomStyle() : useStyles();
  let subheaderExist = false;
  let filteredItems = items
    .filter((item) => {
      if (item.subheader !== undefined) {
        subheaderExist = true;
      }
      if (cleanFilter) {
        return (
          cleanup(item.label).indexOf(cleanFilter) > -1 ||
          cleanup(item.value).indexOf(cleanFilter) > -1
        );
      }
      return true;
    })
    .filter((_item, idx) => {
      return idx < (maxItems || items.length);
    });

  if (sortComparefn !== undefined) {
    filteredItems = filteredItems.sort(sortComparefn);
  }

  const menuItems = disableAlphanumSort
    ? filteredItems
    : filteredItems.sort((a, b) => compareAlphanums(a.label, b.label));

  return (
    <>
      <FormControl fullWidth>
        {label !== undefined && <InputLabel style={{ whiteSpace: 'nowrap' }}>{label}</InputLabel>}
        <Select
          labelId={label}
          id={name}
          value={value}
          label={label}
          onChange={(e) => {
            onChange(name, String(e.target.value));
          }}
          style={{ ...style }}
          MenuProps={{
            classes: { paper: classes.menuPaper },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
        >
          {subheaderExist
            ? SelectDropdownWithSubheaderView(menuItems).map((ele) => ele)
            : menuItems.map((item) => {
                return (
                  <MenuItem value={item.value}>
                    <Tooltip title={isItemTooltipDisabled ? '' : item.label}>
                      <div>
                        <Text
                          tKey={item.tKey ? item.tKey : ''}
                          style={
                            itemWidth
                              ? {
                                  width:
                                    typeof itemWidth !== 'number' ? itemWidth : `${itemWidth}px`,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : {}
                          }
                        >
                          {item.label}
                        </Text>
                      </div>
                    </Tooltip>
                  </MenuItem>
                );
              })}
        </Select>
      </FormControl>
      {helperText && <FormHelperText style={error ? errorColor : {}}>{helperText}</FormHelperText>}
    </>
  );
};
